import { useEffect, useRef } from "react";

const NODES = [
  { id: "monitor", label: "Monitoring", x: 0.08, y: 0.30 },
  { id: "assess", label: "Assessment", x: 0.30, y: 0.62 },
  { id: "decide", label: "Decision Making", x: 0.52, y: 0.30 },
  { id: "intervene", label: "Intervention", x: 0.74, y: 0.62 },
  { id: "reassess", label: "Reassessment", x: 0.92, y: 0.30 },
];

const EDGES: [string, string][] = [
  ["monitor", "assess"],
  ["assess", "decide"],
  ["decide", "intervene"],
  ["intervene", "reassess"],
  ["reassess", "monitor"],
  ["decide", "assess"],
  ["intervene", "decide"],
];

const SECTOR_IMAGES = [
  { url: "https://images.pexels.com/photos/265090/pexels-photo-265090.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.06, y: 0.78, label: "Safety Personnel" },
  { url: "https://images.pexels.com/photos/8617715/pexels-photo-8617715.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.22, y: 0.12, label: "Schools" },
  { url: "https://images.pexels.com/photos/8089062/pexels-photo-8089062.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.40, y: 0.85, label: "Police" },
  { url: "https://images.pexels.com/photos/714946/pexels-photo-714946.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.58, y: 0.12, label: "Trains" },
  { url: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.76, y: 0.85, label: "Business" },
  { url: "https://images.pexels.com/photos/440731/pexels-photo-440731.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.94, y: 0.12, label: "Agriculture" },
  { url: "https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.15, y: 0.45, label: "Healthcare" },
  { url: "https://images.pexels.com/photos/414612/pexels-photo-414612.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.35, y: 0.50, label: "Energy" },
  { url: "https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.50, y: 0.48, label: "Manufacturing" },
  { url: "https://images.pexels.com/photos/159490/pexels-photo-159490.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.65, y: 0.45, label: "Maritime" },
  { url: "https://images.pexels.com/photos/2026324/pexels-photo-2026324.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.82, y: 0.50, label: "Aviation" },
  { url: "https://images.pexels.com/photos/159306/pexels-photo-159306.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.96, y: 0.50, label: "Construction" },
  { url: "https://images.pexels.com/photos/264740/pexels-photo-264740.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.05, y: 0.15, label: "Cybersecurity" },
  { url: "https://images.pexels.com/photos/236705/pexels-photo-236705.jpeg?auto=compress&cs=tinysrgb&w=600", x: 0.93, y: 0.78, label: "Government" },
];

interface Particle {
  edgeIdx: number;
  progress: number;
  speed: number;
  reverse: boolean;
}

export function HeroFlowBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = window.devicePixelRatio || 1;

    const PULSE_PERIOD_MS = 5000;
    const baseSpeed = 1 / PULSE_PERIOD_MS;

    const particles: Particle[] = [];
    for (let i = 0; i < EDGES.length; i++) {
      for (let j = 0; j < 3; j++) {
        particles.push({
          edgeIdx: i,
          progress: Math.random(),
          speed: baseSpeed * (0.85 + Math.random() * 0.3),
          reverse: Math.random() > 0.5,
        });
      }
    }

    const imageCache: Record<string, HTMLImageElement> = {};
    for (const sector of SECTOR_IMAGES) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = sector.url;
      imageCache[sector.url] = img;
    }

    function resize() {
      if (!canvas) return;
      const parent = canvas.parentElement;
      if (!parent) return;
      width = parent.clientWidth;
      height = parent.clientHeight;
      dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);

    function nodePos(id: string) {
      const n = NODES.find((nd) => nd.id === id)!;
      return { x: n.x * width, y: n.y * height };
    }

    let lastTime = performance.now();

    function draw(time: number) {
      const dt = Math.min(time - lastTime, 50);
      lastTime = time;
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, width, height);

      // Semi-dark but light background gradient
      const bgGrad = ctx.createLinearGradient(0, 0, width, height);
      bgGrad.addColorStop(0, "#0a1628");
      bgGrad.addColorStop(0.5, "#0f1d35");
      bgGrad.addColorStop(1, "#0a1628");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Subtle grid
      ctx.strokeStyle = "rgba(59,130,246,0.04)";
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 40) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // Draw sector images as circular vignettes
      for (const sector of SECTOR_IMAGES) {
        const sx = sector.x * width;
        const sy = sector.y * height;
        const r = Math.min(width, height) * 0.085;

        ctx.save();
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.clip();

        const img = imageCache[sector.url];
        if (img && img.complete && img.naturalWidth > 0) {
          const ar = img.naturalWidth / img.naturalHeight;
          const iw = ar >= 1 ? r * 2 : r * 2 * ar;
          const ih = ar >= 1 ? r * 2 / ar : r * 2;
          ctx.drawImage(img, sx - iw / 2, sy - ih / 2, iw, ih);
        } else {
          ctx.fillStyle = "rgba(30,58,95,0.3)";
          ctx.fillRect(sx - r, sy - r, r * 2, r * 2);
        }

        ctx.restore();

        // Ring around sector
        ctx.strokeStyle = "rgba(59,130,246,0.2)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Draw edges
      for (const [from, to] of EDGES) {
        const p1 = nodePos(from);
        const p2 = nodePos(to);

        // Curved edge
        const mx = (p1.x + p2.x) / 2;
        const my = (p1.y + p2.y) / 2;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const cx = mx - dy * 0.15;
        const cy = my + dx * 0.15;

        ctx.strokeStyle = "rgba(59,130,246,0.15)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.quadraticCurveTo(cx, cy, p2.x, p2.y);
        ctx.stroke();
      }

      // Draw nodes
      for (const node of NODES) {
        const nx = node.x * width;
        const ny = node.y * height;

        // Glow
        const glowR = 60;
        const glow = ctx.createRadialGradient(nx, ny, 0, nx, ny, glowR);
        glow.addColorStop(0, "rgba(59,130,246,0.25)");
        glow.addColorStop(1, "rgba(59,130,246,0)");
        ctx.fillStyle = glow;
        ctx.fillRect(nx - glowR, ny - glowR, glowR * 2, glowR * 2);

        // Node circle
        const nodeR = 20;
        ctx.fillStyle = "rgba(15,29,53,0.95)";
        ctx.strokeStyle = "rgba(96,165,250,0.6)";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(nx, ny, nodeR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Inner pulse - once per 5 seconds
        const pulsePhase = (2 * Math.PI) / 5000;
        const pulse = 0.5 + 0.5 * Math.sin(time * pulsePhase + NODES.indexOf(node) * 0.7);
        ctx.fillStyle = `rgba(96,165,250,${0.3 + pulse * 0.4})`;
        ctx.beginPath();
        ctx.arc(nx, ny, 8 + pulse * 4, 0, Math.PI * 2);
        ctx.fill();

        // Label
        ctx.fillStyle = "rgba(191,219,254,0.9)";
        ctx.font = "700 14px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(node.label, nx, ny + nodeR + 6);
      }

      // Update and draw particles (dots moving along edges)
      for (const p of particles) {
        const [fromId, toId] = EDGES[p.edgeIdx];
        const from = nodePos(fromId);
        const to = nodePos(toId);

        const mx = (from.x + to.x) / 2;
        const my = (from.y + to.y) / 2;
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const cx = mx - dy * 0.15;
        const cy = my + dx * 0.15;

        p.progress += p.speed * dt;
        if (p.progress > 1) {
          p.progress = 0;
          p.reverse = !p.reverse;
        }

        const t = p.reverse ? 1 - p.progress : p.progress;

        // Quadratic bezier point
        const px = (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * cx + t * t * to.x;
        const py = (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * cy + t * t * to.y;

        // Particle glow
        const pGlow = ctx.createRadialGradient(px, py, 0, px, py, 12);
        pGlow.addColorStop(0, "rgba(125,211,252,0.8)");
        pGlow.addColorStop(1, "rgba(125,211,252,0)");
        ctx.fillStyle = pGlow;
        ctx.fillRect(px - 12, py - 12, 24, 24);

        // Particle dot
        ctx.fillStyle = "rgba(186,230,253,0.95)";
        ctx.beginPath();
        ctx.arc(px, py, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
}
